import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Search, Ban, RefreshCw, Building2, Users, 
  TrendingUp, Phone, Mail, MapPin, Calendar, Clock,
  CheckCircle, AlertTriangle, Star, ThumbsUp, Eye,
  ChevronRight, BarChart3, Filter, Undo2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  new: { label: 'Neu', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', icon: Building2 },
  visited: { label: 'Besucht', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Clock },
  interested: { label: 'Interessiert', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: ThumbsUp },
  follow_up: { label: 'Follow-up', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Calendar },
  not_interested: { label: 'Kein Interesse', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: Ban },
  partner: { label: 'Partner', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: Star }
};

const AdminCRMPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const { isDarkMode } = useTheme();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [token, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, contactsRes, blacklistRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/crm/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/crm/contacts?limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/crm/blacklist`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (contactsRes.ok) {
        const data = await contactsRes.json();
        setContacts(data.contacts || []);
      }
      if (blacklistRes.ok) {
        const data = await blacklistRes.json();
        setBlacklist(data.blacklist || []);
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromBlacklist = async (placeId) => {
    if (!window.confirm('Betrieb wirklich von der Blacklist entfernen?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/crm/blacklist/${placeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        loadData();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleViewContact = async (placeId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/crm/contact/${placeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedContact(data);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const filteredContacts = contacts.filter(c => {
    const matchesSearch = !searchTerm || 
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.address?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredBlacklist = blacklist.filter(b => 
    !searchTerm || 
    b.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        <RefreshCw className="animate-spin w-8 h-8 text-brand-green" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'} border-b sticky top-0 z-40`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin/dashboard')}
                className="p-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold">CRM & Kontaktverwaltung</h1>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Alle Vertriebskontakte und Blacklist verwalten
                </p>
              </div>
            </div>
            <Button onClick={loadData} variant="outline" className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Aktualisieren
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 shadow-sm`}>
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Building2 className="w-4 h-4" />
                <span className="text-xs">Gesamt</span>
              </div>
              <p className="text-2xl font-bold">{stats.total_contacts}</p>
            </div>
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 shadow-sm`}>
              <div className="flex items-center gap-2 text-emerald-500 mb-1">
                <Star className="w-4 h-4" />
                <span className="text-xs">Partner</span>
              </div>
              <p className="text-2xl font-bold text-emerald-600">{stats.partner_count}</p>
            </div>
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 shadow-sm`}>
              <div className="flex items-center gap-2 text-yellow-500 mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-xs">Follow-ups</span>
              </div>
              <p className="text-2xl font-bold text-yellow-600">{stats.follow_up_count}</p>
            </div>
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 shadow-sm`}>
              <div className="flex items-center gap-2 text-red-500 mb-1">
                <Ban className="w-4 h-4" />
                <span className="text-xs">Blacklist</span>
              </div>
              <p className="text-2xl font-bold text-red-600">{stats.blacklist_count}</p>
            </div>
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 shadow-sm`}>
              <div className="flex items-center gap-2 text-blue-500 mb-1">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs">7 Tage</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{stats.recent_total}</p>
            </div>
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 shadow-sm`}>
              <div className="flex items-center gap-2 text-purple-500 mb-1">
                <BarChart3 className="w-4 h-4" />
                <span className="text-xs">Quote</span>
              </div>
              <p className="text-2xl font-bold text-purple-600">
                {stats.total_contacts > 0 
                  ? Math.round((stats.partner_count / stats.total_contacts) * 100) 
                  : 0}%
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm mb-6`}>
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            {[
              { id: 'overview', label: 'Übersicht', icon: BarChart3 },
              { id: 'contacts', label: 'Alle Kontakte', icon: Building2 },
              { id: 'blacklist', label: 'Blacklist', icon: Ban }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-brand-green border-b-2 border-brand-green'
                      : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {tab.id === 'blacklist' && blacklist.length > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {blacklist.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="p-6">
            {/* Search */}
            <div className="flex gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Betrieb suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-200'
                  }`}
                />
              </div>
              {activeTab === 'contacts' && (
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={`px-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-200'
                  }`}
                >
                  <option value="">Alle Status</option>
                  {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && stats && (
              <div className="space-y-6">
                {/* Top Salespeople */}
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-brand-green" />
                    Top Vertriebler
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                          <th className="text-left py-2 px-4">#</th>
                          <th className="text-left py-2 px-4">Name</th>
                          <th className="text-right py-2 px-4">Kontakte</th>
                          <th className="text-right py-2 px-4">Partner</th>
                          <th className="text-right py-2 px-4">Quote</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.top_salespeople?.map((sp, idx) => (
                          <tr key={sp.id} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                            <td className="py-3 px-4">
                              {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                            </td>
                            <td className="py-3 px-4 font-medium">{sp.name}</td>
                            <td className="py-3 px-4 text-right">{sp.total_contacts}</td>
                            <td className="py-3 px-4 text-right text-emerald-600">{sp.partners}</td>
                            <td className="py-3 px-4 text-right">
                              <span className={`px-2 py-1 rounded text-sm ${
                                sp.conversion_rate >= 10 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : sp.conversion_rate >= 5 
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {sp.conversion_rate}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Status Distribution */}
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-brand-green" />
                    Status-Verteilung
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                      const Icon = config.icon;
                      const count = stats.status_counts?.[key] || 0;
                      return (
                        <div 
                          key={key}
                          className={`${config.color} rounded-lg p-3 cursor-pointer hover:opacity-80`}
                          onClick={() => {
                            setActiveTab('contacts');
                            setStatusFilter(key);
                          }}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Icon className="w-4 h-4" />
                            <span className="text-sm font-medium">{config.label}</span>
                          </div>
                          <p className="text-2xl font-bold">{count}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Contacts Tab */}
            {activeTab === 'contacts' && (
              <div className="space-y-3">
                {filteredContacts.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Keine Kontakte gefunden</p>
                  </div>
                ) : (
                  filteredContacts.map(contact => {
                    const StatusIcon = STATUS_CONFIG[contact.status]?.icon || Building2;
                    return (
                      <div 
                        key={contact.place_id}
                        className={`${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-lg p-4 flex items-center justify-between`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${STATUS_CONFIG[contact.status]?.color || 'bg-gray-100'}`}>
                            <StatusIcon className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-medium">{contact.name}</h4>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {contact.address}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Entdeckt von: {contact.discovered_by_name}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded ${STATUS_CONFIG[contact.status]?.color}`}>
                            {STATUS_CONFIG[contact.status]?.label}
                          </span>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleViewContact(contact.place_id)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Blacklist Tab */}
            {activeTab === 'blacklist' && (
              <div className="space-y-3">
                {filteredBlacklist.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                    <p>Keine Betriebe auf der Blacklist</p>
                  </div>
                ) : (
                  filteredBlacklist.map(business => (
                    <div 
                      key={business.place_id}
                      className={`${isDarkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-100'} border rounded-lg p-4`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-red-100 dark:bg-red-900/50 rounded-lg">
                            <Ban className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <h4 className="font-medium">{business.name}</h4>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <MapPin className="w-3 h-3 inline mr-1" />
                              {business.address}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Blockiert von: {business.blocked_by_name} • 
                              {business.status_updated_at && new Date(business.status_updated_at).toLocaleDateString('de-DE')}
                            </p>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="gap-2 text-emerald-600 border-emerald-600 hover:bg-emerald-50"
                          onClick={() => handleRemoveFromBlacklist(business.place_id)}
                        >
                          <Undo2 className="w-4 h-4" />
                          Reaktivieren
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Detail Modal */}
      {showDetailModal && selectedContact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowDetailModal(false)}>
          <div 
            className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto`}
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">Kontaktdetails</h3>
                <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-lg">{selectedContact.name}</h4>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {selectedContact.address}
                  </p>
                </div>

                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${STATUS_CONFIG[selectedContact.status]?.color}`}>
                  {(() => {
                    const Icon = STATUS_CONFIG[selectedContact.status]?.icon || Building2;
                    return <Icon className="w-4 h-4" />;
                  })()}
                  <span className="font-medium">{STATUS_CONFIG[selectedContact.status]?.label}</span>
                </div>

                {selectedContact.contact_person && (
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span>{selectedContact.contact_person}</span>
                  </div>
                )}
                
                {selectedContact.contact_phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <a href={`tel:${selectedContact.contact_phone}`} className="text-brand-green">
                      {selectedContact.contact_phone}
                    </a>
                  </div>
                )}
                
                {selectedContact.contact_email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <a href={`mailto:${selectedContact.contact_email}`} className="text-brand-green">
                      {selectedContact.contact_email}
                    </a>
                  </div>
                )}

                {/* Visit History */}
                {selectedContact.visit_history?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Besuchshistorie</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {selectedContact.visit_history.slice().reverse().map((visit, idx) => (
                        <div key={idx} className={`text-sm p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <div className="flex justify-between">
                            <span className="font-medium">{visit.salesperson_name}</span>
                            <span className="text-gray-400">
                              {new Date(visit.date).toLocaleDateString('de-DE')}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {STATUS_CONFIG[visit.previous_status]?.label} → {STATUS_CONFIG[visit.new_status]?.label}
                          </div>
                          {visit.notes && (
                            <p className="text-gray-500 mt-1">{visit.notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCRMPage;
